import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import "../../App.css";
import Footer from "../Footer";
import Reveal from "../Reveal";
import Lightbox from "../Lightbox";
import Seo from "../Seo";
import events, {
	eventTypes,
	typeLabel,
	serviceLabel,
} from "../../data/portfolio";
import "./Portfolio.css";

function Portfolio() {
	const [filter, setFilter] = useState("all");
	const [lightbox, setLightbox] = useState({ open: false, event: null });

	const filtered = useMemo(
		() =>
			filter === "all" ? events : events.filter((e) => e.type === filter),
		[filter]
	);

	const featured = filtered.filter((e) => e.featured);
	const grid = filtered.filter((e) => !e.featured);

	const openLightbox = (event) => setLightbox({ open: true, event });
	const closeLightbox = () => setLightbox({ open: false, event: null });

	return (
		<>
			<Seo
				title="Portfolio"
				description="Inspiratie uit eerdere feesten: verjaardagen, bruiloften, bedrijfsfeesten en communies die wij mochten aankleden."
				path="/portfolio"
			/>
			<section className="portfolio-hero">
				<div className="portfolio-hero-content">
					<span className="portfolio-eyebrow">ONZE EVENEMENTEN</span>
					<h1 className="portfolio-title">
						Inspiratie uit eerdere feesten
					</h1>
					<div className="portfolio-rule" aria-hidden="true"></div>
					<p className="portfolio-subtitle">
						Een glimp van de evenementen die wij mochten aankleden — van
						intieme verjaardagen tot zakelijke avonden.
					</p>
				</div>
			</section>

			<nav
				className="portfolio-filters"
				aria-label="Filter evenementen op type">
				<div className="portfolio-filter-strip">
					{eventTypes.map((t) => (
						<button
							key={t.slug}
							type="button"
							className={`portfolio-filter ${
								filter === t.slug ? "is-active" : ""
							}`}
							onClick={() => setFilter(t.slug)}
							aria-pressed={filter === t.slug}>
							{t.label}
						</button>
					))}
				</div>
			</nav>

			{filtered.length === 0 && (
				<section className="portfolio-empty">
					<p>Nog geen evenementen in deze categorie.</p>
				</section>
			)}

			{featured.length > 0 && (
				// Keyed by filter: Reveal observes its stagger children once on
				// mount, so tiles mounted by a later tab switch would otherwise
				// never be observed and stay at their hidden rest state.
				<Reveal key={filter} as="section" className="portfolio-featured">
					{featured.map((event, i) => (
						<article
							key={event.slug}
							className={`portfolio-featured-event ${
								i % 2 === 1 ? "is-reversed" : ""
							}`}>
							<button
								type="button"
								className="portfolio-featured-image stagger-clip"
								style={{ "--stagger-index": 0 }}
								onClick={() => openLightbox(event)}
								aria-label={`Bekijk fotogalerij van ${event.title}`}>
								<img
									src={event.photos[0].src}
									alt={event.photos[0].alt}
									loading="lazy"
								/>
							</button>

							<div className="portfolio-featured-text">
								<span className="portfolio-featured-eyebrow">
									{typeLabel(event.type)}
								</span>
								<h2 className="portfolio-featured-title">
									{event.title}
								</h2>
								<div
									className="portfolio-featured-rule"
									aria-hidden="true"></div>
								<p className="portfolio-featured-caption">
									{event.caption}
								</p>
								<div className="portfolio-featured-services">
									{event.services.map((s) => (
										<span
											key={s}
											className="portfolio-featured-service">
											{serviceLabel(s)}
										</span>
									))}
								</div>
								<button
									type="button"
									className="portfolio-featured-cta"
									onClick={() => openLightbox(event)}>
									Bekijk alle foto's{" "}
									<span
										aria-hidden="true"
										className="portfolio-featured-cta-arrow">
										→
									</span>
								</button>
							</div>
						</article>
					))}
				</Reveal>
			)}

			{grid.length > 0 && (
				<Reveal
					key={`grid-${filter}`}
					as="section"
					className="portfolio-grid-section reveal--stagger">
					<div className="portfolio-grid">
						{grid.map((event, i) => (
							<button
								key={event.slug}
								type="button"
								className="portfolio-grid-tile stagger-item"
								style={{ "--stagger-index": i }}
								onClick={() => openLightbox(event)}
								aria-label={`Bekijk fotogalerij van ${event.title}`}>
								<div className="portfolio-grid-imagewrap">
									<img
										src={event.photos[0].src}
										alt={event.photos[0].alt}
										loading="lazy"
									/>
								</div>
								<div className="portfolio-grid-text">
									<span className="portfolio-grid-eyebrow">
										{typeLabel(event.type)}
									</span>
									<h3 className="portfolio-grid-title">
										{event.title}
									</h3>
								</div>
							</button>
						))}
					</div>
					<div
						className="portfolio-grid-cta stagger-item"
						style={{ "--stagger-index": grid.length }}>
						<Link to="/aanvraag" className="portfolio-grid-cta-link">
							Uw feest tussen deze foto's? Start uw aanvraag{" "}
							<span
								aria-hidden="true"
								className="portfolio-grid-cta-arrow">
								→
							</span>
						</Link>
					</div>
				</Reveal>
			)}

			{lightbox.open && (
				<Lightbox event={lightbox.event} onClose={closeLightbox} />
			)}

			<Footer />
		</>
	);
}

export default Portfolio;
